"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.applyPermissionSetSchema = exports.updatePermissionSetSchema = exports.createPermissionSetSchema = exports.employeeListQuerySchema = exports.changeOwnPasswordSchema = exports.resetEmployeePasswordSchema = exports.updateEmployeeSchema = exports.createEmployeeSchema = exports.employeeUsernameSchema = exports.permissionKeyArray = void 0;
exports.effectivePermissions = effectivePermissions;
const zod_1 = require("zod");
const permissions_1 = require("./permissions");
/* -------------------------------------------------------------------------- */
/* Privileges                                                                 */
/* -------------------------------------------------------------------------- */
/**
 * A submitted privileges matrix.
 *
 * Rejects unknown keys with the offending value in the message, so the API
 * returns something actionable instead of a generic "invalid body".
 */
exports.permissionKeyArray = zod_1.z
    .array(zod_1.z.string().superRefine((value, ctx) => {
    if (!(0, permissions_1.isPermissionKey)(value)) {
        ctx.addIssue({
            code: 'custom',
            message: `Unknown permission key "${value}".`,
        });
    }
}))
    // The matrix is a set: two ticks of the same box must not persist twice.
    .transform((keys) => [...new Set(keys)]);
/** Effective grants for a caller — owners hold everything (SPEC §3). */
function effectivePermissions(isOwner, stored) {
    if (isOwner)
        return [...permissions_1.PERMISSION_KEYS];
    return stored.filter(permissions_1.isPermissionKey);
}
/* -------------------------------------------------------------------------- */
/* Write models                                                               */
/* -------------------------------------------------------------------------- */
/**
 * Usernames are the login identifier and are unique per venue, so they are
 * normalised to lower case; display names are never touched.
 */
const username = zod_1.z
    .string()
    .trim()
    .min(3, 'Username must be at least 3 characters.')
    .max(40)
    .regex(/^[a-zA-Z0-9._-]+$/, 'Use letters, numbers, dot, dash or underscore only.')
    .transform((value) => value.toLowerCase());
exports.employeeUsernameSchema = username;
/**
 * Minimum length only. No composition rules: forced symbol classes push venue
 * staff toward written-down passwords, and the hash is Argon2id either way.
 */
const password = zod_1.z.string().min(8, 'Password must be at least 8 characters.').max(200);
/** Free-form: Iraqi numbers are entered with and without country code. */
const phone = zod_1.z.string().trim().max(30).optional().nullable();
exports.createEmployeeSchema = zod_1.z.object({
    // NOT trimmed to a stricter charset — Arabic names must survive verbatim.
    name: zod_1.z.string().trim().min(1, 'Name is required.').max(120),
    phone,
    username,
    password,
    avatarMediaAssetId: zod_1.z.string().min(1).optional().nullable(),
    permissions: exports.permissionKeyArray.default([]),
    /** Wave 3 — the template link; the GRANT is always the permissions array. */
    permissionSetId: zod_1.z.string().min(1).optional().nullable(),
    /** Whole percent; null/omitted = no ceiling, 0 = no discounting at all. */
    maxDiscountPercent: zod_1.z.number().int().min(0).max(100).optional().nullable(),
    isActive: zod_1.z.boolean().default(true),
});
/**
 * Partial update. Password is deliberately absent — it moves through
 * {@link resetEmployeePasswordSchema} so the "Reset Password" column is a
 * distinct, separately-permissioned action rather than a field that can ride
 * along unnoticed inside a profile edit.
 */
exports.updateEmployeeSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1).max(120).optional(),
    phone,
    username: username.optional(),
    avatarMediaAssetId: zod_1.z.string().min(1).optional().nullable(),
    permissions: exports.permissionKeyArray.optional(),
    /** Wave 3 — null detaches the template; the grant array is untouched. */
    permissionSetId: zod_1.z.string().min(1).optional().nullable(),
    /** Whole percent; omitted leaves the ceiling alone, null removes it. */
    maxDiscountPercent: zod_1.z.number().int().min(0).max(100).optional().nullable(),
    isActive: zod_1.z.boolean().optional(),
});
/** SPEC §6.4 — the "Reset Password" column. */
exports.resetEmployeePasswordSchema = zod_1.z.object({
    password,
});
/**
 * Any employee changes their OWN password (owner, 2026-09-23: "all the
 * employees should be able to change their account password").
 *
 * The current password is required even though the caller is signed in: a
 * till left unlocked must not let the next person lock its owner out. It is
 * checked only for presence here — its length rules are whatever they were
 * when it was set, and the same 200 the login form accepts, so a long existing
 * password can always be typed back in. The new one follows the same rule as
 * every other password this module sets.
 */
exports.changeOwnPasswordSchema = zod_1.z
    .object({
    currentPassword: zod_1.z.string().min(1, 'Enter your current password.').max(200),
    newPassword: password,
})
    .refine((value) => value.newPassword !== value.currentPassword, {
    message: 'Choose a password different from the current one.',
    path: ['newPassword'],
});
/* -------------------------------------------------------------------------- */
/* Query                                                                      */
/* -------------------------------------------------------------------------- */
/** SPEC §6.4 header: search by name OR phone. */
exports.employeeListQuerySchema = zod_1.z.object({
    search: zod_1.z.string().trim().max(120).optional(),
    includeInactive: zod_1.z.coerce.boolean().default(false),
});
const permissionSetName = zod_1.z.string().trim().min(1, 'Name the role.').max(60);
exports.createPermissionSetSchema = zod_1.z.object({
    name: permissionSetName,
    description: zod_1.z.string().trim().max(300).optional(),
    permissions: zod_1.z.array(zod_1.z.string()).max(200),
});
/** Explicit — never `.partial()` (zod 4 keeps `.default()`s). */
exports.updatePermissionSetSchema = zod_1.z
    .object({
    name: permissionSetName.optional(),
    description: zod_1.z.string().trim().max(300).nullable().optional(),
    permissions: zod_1.z.array(zod_1.z.string()).max(200).optional(),
})
    .refine((value) => Object.keys(value).length > 0, { message: 'Nothing to change.' });
/** The explicit re-sync: push the template's CURRENT permissions to members. */
exports.applyPermissionSetSchema = zod_1.z.object({}).strict();
//# sourceMappingURL=employees.js.map